/** Product 360 — ONE Product Content panel: find, freshness, deltas,
 *  technical specifications and document/image import. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Check, X, FileText, Image as ImageIcon } from "lucide-react";

const FRESH_TONE = {
  CURRENT: "bg-emerald-100 text-emerald-800",
  UPDATE_AVAILABLE: "bg-amber-100 text-amber-800",
  REVIEW_REQUIRED: "bg-violet-100 text-violet-800",
  NO_SOURCE_CONTENT: "bg-slate-100 text-slate-500",
};

export default function ProductContentPanel({ productId }) {
  const [data, setData] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => Promise.all([
    api.get(`/pc/content/${productId}`).then(({ data }) => setData(data)),
    api.get(`/pc/specs/${productId}`).then(({ data }) => setSpecs(data)),
  ]).catch(() => {}), [productId]);
  useEffect(() => { load(); }, [load]);

  const find = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/pc/find/${productId}`);
      if (res.match_status === "EXACT_MATCH") toast.success(`Content found via ${res.provider}`);
      else if (res.match_status === "SETUP_REQUIRED")
        toast.warning("No content provider configured — set up Open Icecat in ONE Connect → Product Content");
      else toast.info(`${res.match_status.replace(/_/g, " ")}${res.message ? ` — ${res.message}` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };

  const importSel = async (payload) => {
    const provider = data?.items?.[0]?.provider;
    if (!provider) return;
    try {
      const { data: r } = await api.post(`/pc/import/${productId}`, { provider, ...payload });
      toast.success(`Imported: ${r.fields_applied.length} field(s), ${r.documents_created} document(s) — docs need customer-safe review`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  const content = data?.items?.[0];

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="product-content-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="h-4 w-4 text-emerald-700" />
        <span className="text-[13px] font-semibold text-slate-800">Product Content</span>
        {data && (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${FRESH_TONE[data.freshness] || ""}`}
            data-testid="pc-freshness-badge">{(data.freshness || "").replace(/_/g, " ")}</span>)}
        {content && (
          <span className="text-[10px] text-slate-500">
            {content.provider} · {content.country} · retrieved {(content.retrieved_at || "").slice(0, 10)}</span>)}
        <button onClick={find} disabled={busy} data-testid="pc-find-btn"
          className="ml-auto h-8 px-3 text-xs bg-emerald-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} />
          {busy ? "Searching providers…" : "Find Product Information"}</button>
      </div>

      {data?.state?.match_status === "SETUP_REQUIRED" && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
          SETUP REQUIRED — no external content provider is configured yet.{" "}
          <a href="/admin/one-connect/product-content" className="font-semibold underline">Configure Open Icecat</a>
        </div>)}

      {content && (
        <>
          <div className="text-[12px] text-slate-700">
            <span className="font-semibold">{content.title}</span>
            {content.short_description && (
              <p className="text-[11px] text-slate-500 mt-1">{content.short_description}</p>)}
          </div>

          {(data.field_deltas || []).length > 0 && (
            <div className="border border-violet-200 rounded-md p-2.5 space-y-1.5" data-testid="pc-field-deltas">
              <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">
                Proposed updates — review before accepting</div>
              {data.field_deltas.map((d) => (
                <div key={d.field} className="flex items-start gap-2 text-[11px]">
                  <span className="font-mono font-semibold w-20 shrink-0">{d.field}</span>
                  <span className="text-slate-400 line-through flex-1 truncate">{d.current || "(empty)"}</span>
                  <span className="text-slate-800 flex-1">{String(d.proposed).slice(0, 140)}</span>
                  <button onClick={() => importSel({ accept_fields: [d.field] })}
                    data-testid={`pc-accept-${d.field}`}
                    className="p-1 rounded bg-emerald-100 text-emerald-800"><Check className="h-3 w-3" /></button>
                </div>))}
            </div>)}

          <div className="flex gap-2 flex-wrap text-[11px]">
            {(content.documents || []).length > 0 && (
              <button onClick={() => importSel({ import_documents: true })}
                data-testid="pc-import-docs-btn"
                className="h-8 px-3 border border-slate-300 rounded inline-flex items-center gap-1.5 hover:bg-emerald-50">
                <FileText className="h-3 w-3" /> Import {content.documents.length} document(s) to Tech Library</button>)}
            {(content.images || []).length > 0 && (
              <button onClick={() => importSel({ import_images: true })}
                data-testid="pc-import-images-btn"
                className="h-8 px-3 border border-slate-300 rounded inline-flex items-center gap-1.5 hover:bg-emerald-50">
                <ImageIcon className="h-3 w-3" /> Import product image(s)</button>)}
          </div>

          {(content.images || []).length > 0 && (
            <div className="flex gap-2">
              {content.images.slice(0, 4).map((img, i) => (
                <img key={i} src={img.url} alt={content.title}
                  className="h-20 w-20 object-contain border border-slate-200 rounded bg-white" />))}
            </div>)}
        </>)}

      {specs && (specs.groups || []).length > 0 && (
        <div data-testid="pc-tech-specs">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Technical Specifications
            <span className="ml-2 text-slate-400 normal-case">via {specs.provider} · {specs.country}</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {specs.groups.map((g) => (
              <div key={g.group} className="border border-slate-100 rounded p-2.5">
                <div className="text-[11px] font-semibold text-slate-700 mb-1">{g.group}</div>
                {(g.features || []).map((f) => (
                  <div key={f.name} className="flex text-[11px] py-0.5 border-b border-slate-50 last:border-0">
                    <span className="text-slate-500 w-1/2 shrink-0">{f.name}</span>
                    <span className="text-slate-800">{f.value ?? "—"}</span>
                  </div>))}
              </div>))}
          </div>
        </div>)}

      {content?.lifecycle && (content.lifecycle.release_date || content.lifecycle.end_of_life) && (
        <div className="text-[10px] text-slate-500">
          Lifecycle: {content.lifecycle.release_date && `Released ${content.lifecycle.release_date}`}
          {content.lifecycle.end_of_life && ` · End of life ${content.lifecycle.end_of_life}`}
        </div>)}
    </div>
  );
}
